import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';

const SessionTimeoutDialog = ({ 
  open, 
  timeLeft, 
  formatTime, 
  onConfirm, 
  onLogout 
}) => {
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        color: 'white',
        borderRadius: '12px 12px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <WarningIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Session Timeout Warning
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: 24
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            You have been inactive for 20 minutes.
          </Typography>
        </Alert>
        
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your session will expire in:
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1,
            mb: 2
          }}>
            <TimeIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                color: timeLeft <= 30 ? 'error.main' : 'warning.main',
                fontFamily: 'monospace'
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Click "I'm Here" to extend your session, or you will be automatically logged out.
          </Typography>
        </Box>
        
        {timeLeft <= 30 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1,
            mt: 2,
            p: 2,
            backgroundColor: 'error.light',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.main'
          }}>
            <CircularProgress 
              size={20} 
              sx={{ color: 'error.main' }} 
            />
            <Typography variant="body2" color="error.dark" sx={{ fontWeight: 500 }}>
              Final warning! Session will expire soon.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 1,
        justifyContent: 'center',
        gap: 2
      }}>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          I'm Here - Extend Session
        </Button>
        
        <Button
          onClick={onLogout}
          variant="outlined"
          color="error"
          size="large"
          startIcon={<LogoutIcon />}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              backgroundColor: 'error.light',
              color: 'error.dark'
            }
          }}
        >
          Logout Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutDialog;
